import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

import {
  AnimationEvent,
  trigger,
  state,
  style,
  animate,
  transition
} from '@angular/animations';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/fromEvent';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import {
  SkyWindowRefService
} from '../window';

import {
  SkyPopoverAlignment,
  SkyPopoverPlacement
} from './types';

import { SkyPopoverAdapterService } from './popover-adapter.service';


export class SkyPopoverComponent implements OnInit, OnDestroy {
  
  public dismissOnBlur = true;

  
  public popoverTitle: string;

  
  public set alignment(value: SkyPopoverAlignment) {
    this._alignment = value;
  }

  public get alignment(): SkyPopoverAlignment {
    return this._alignment || 'center';
  }

  
  public set placement(value: SkyPopoverPlacement) {
    this._placement = value;
  }

  public get placement(): SkyPopoverPlacement {
    return this._placement || 'above';
  }

  
  public popoverOpened = new EventEmitter<SkyPopoverComponent>();

  
  public popoverClosed = new EventEmitter<SkyPopoverComponent>();

  
  public popoverContainer: ElementRef;

  
  public popoverArrow: ElementRef;

  public isOpen = false;
  public isVisible = false;
  public isMouseEnter = false;
  public classNames: string[] = [];
  public animationState: 'hidden' | 'visible' = 'hidden';

  public popoverTop: number;
  public popoverLeft: number;
  public arrowTop: number;
  public arrowLeft: number;

  private caller: ElementRef;
  private idled = new Subject<boolean>();
  private isMarkedForCloseOnMouseLeave = false;
  private preferredPlacement: SkyPopoverPlacement;
  private scrollListeners: Function[] = [];

  private _alignment: SkyPopoverAlignment;
  private _placement: SkyPopoverPlacement;

  constructor(
    private adapterService: SkyPopoverAdapterService,
    private changeDetector: ChangeDetectorRef,
    private elementRef: ElementRef,
    private windowRef: SkyWindowRefService
  ) { }

  public ngOnInit() {
    this.preferredPlacement = this.placement;
    this.adapterService.hidePopover(this.popoverContainer);
  }

  public ngOnDestroy() {
    this.removeListeners();
    this.idled.complete();
  }

  public positionNextTo(
    caller: ElementRef,
    placement?: SkyPopoverPlacement,
    alignment?: SkyPopoverAlignment
  ) {
    if (!caller) {
      return;
    }

    this.close();

    this.caller = caller;
    this.placement = placement;
    this.alignment = alignment;
    this.preferredPlacement = this.placement;
    this.changeDetector.markForCheck();

    // Let the styles render before gauging the dimensions.
    this.windowRef.getWindow().setTimeout(() => {
      if (this.adapterService.isPopoverLargerThanParent(this.popoverContainer)) {
        this.placement = 'fullscreen';
      }

      this.isVisible = true;
      this.positionPopover();
      this.addListeners();
      this.animationState = 'visible';
      this.changeDetector.markForCheck();
    });
  }

  public reposition() {
    this.placement = this.preferredPlacement;
    this.changeDetector.markForCheck();

    if (this.adapterService.isPopoverLargerThanParent(this.popoverContainer)) {
      this.placement = 'fullscreen';
    }

    this.positionPopover();
  }

  public close() {
    this.animationState = 'hidden';
    this.removeListeners();
    this.changeDetector.markForCheck();
  }

  public onAnimationStart(event: AnimationEvent) {
    if (event.fromState === 'void') {
      return;
    }

    if (event.toState === 'visible') {
      this.adapterService.showPopover(this.popoverContainer);
    }
  }

  public onAnimationDone(event: AnimationEvent) {
    if (event.fromState === 'void') {
      return;
    }

    if (event.toState === 'hidden') {
      this.isOpen = false;
      this.adapterService.hidePopover(this.popoverContainer);
      this.popoverClosed.emit(this);
    } else {
      this.isOpen = true;
      this.popoverOpened.emit(this);
    }
  }

  public markForCloseOnMouseLeave() {
    this.isMarkedForCloseOnMouseLeave = true;
  }

  private positionPopover() {
    if (this.placement !== 'fullscreen') {
      const elements = {
        popover: this.popoverContainer,
        popoverArrow: this.popoverArrow,
        caller: this.caller
      };

      const position = this.adapterService.getPopoverPosition(
        elements,
        this.preferredPlacement,
        this.alignment
      );

      this.placement = position.placement;
      this.alignment = position.alignment;
      this.popoverTop = position.top;
      this.popoverLeft = position.left;
      this.arrowTop = position.arrowTop;
      this.arrowLeft = position.arrowLeft;
    }

    this.changeDetector.markForCheck();
  }

  private addListeners(): void {
    const windowObj = this.windowRef.getWindow();
    const hostElement = this.elementRef.nativeElement;

    Observable
      .fromEvent(windowObj, 'resize')
      .takeUntil(this.idled)
      .subscribe(() => {
        this.reposition();
      });

    Observable
      .fromEvent(windowObj.document, 'focusin')
      .takeUntil(this.idled)
      .subscribe((event: KeyboardEvent) => {
        const targetIsChild = (hostElement.contains(event.target));
        const targetIsCaller = (this.caller && this.caller.nativeElement === event.target);

        /* istanbul ignore else */
        if (!targetIsChild && !targetIsCaller && this.dismissOnBlur) {
          // The popover is currently being operated by the user, and
          // has just lost keyboard focus. We should close it.
          this.close();
        }
      });

    Observable
      .fromEvent(windowObj.document, 'click')
      .takeUntil(this.idled)
      .subscribe((event: MouseEvent) => {
        if (!this.isMouseEnter && this.dismissOnBlur) {
          this.close();
        }
      });

    Observable
      .fromEvent(hostElement, 'mouseenter')
      .takeUntil(this.idled)
      .subscribe(() => {
        this.isMouseEnter = true;
      });

    Observable
      .fromEvent(hostElement, 'mouseleave')
      .takeUntil(this.idled)
      .subscribe(() => {
        this.isMouseEnter = false;
        if (this.isMarkedForCloseOnMouseLeave) {
          this.close();
          this.isMarkedForCloseOnMouseLeave = false;
        }
      });

    Observable
      .fromEvent(hostElement, 'keyup')
      .takeUntil(this.idled)
      .subscribe((event: KeyboardEvent) => {
        const key = event.key.toLowerCase();

        if (key === 'escape') {
          event.stopPropagation();
          event.preventDefault();
          this.close();

          /* istanbul ignore else */
          if (this.caller) {
            this.caller.nativeElement.focus();
          }
        }
      });

    this.scrollListeners = this.adapterService
      .getParentScrollListeners(this.popoverContainer, (isElementVisibleWithinScrollable: boolean) => {
        this.reposition();
        this.isVisible = isElementVisibleWithinScrollable;
        this.changeDetector.markForCheck();
      });
  }

  private removeListeners(): void {
    this.idled.next(true);

    if (this.scrollListeners) {
      this.scrollListeners.forEach((listener: any) => {
        // Remove renderer-generated listeners by calling the listener itself.
        // https://github.com/angular/angular/issues/9368#issuecomment-227199778
        listener();
      });

      this.scrollListeners = [];
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-popover',
  template: `<div class="sky-popover-container"
  tabindex="-1"
  [ngClass]="[
    'sky-popover-alignment-' + alignment,
    'sky-popover-placement-' + placement
  ]"
  (@popoverState.start)="onAnimationStart($event)"
  (@popoverState.done)="onAnimationDone($event)"
  [@popoverState]="animationState"
  [style.top.px]="popoverTop"
  [style.left.px]="popoverLeft"
  [style.visibility]="(isVisible) ? 'visible': 'hidden'"
  #popoverContainer>
  <div class="sky-popover sky-shadow sky-rounded-corners">
    <header class="sky-popover-header" *ngIf="popoverTitle">
      <h1 class="sky-popover-title">
        {{ popoverTitle }}
      </h1>
    </header>
    <div class="sky-popover-body">
      <ng-content></ng-content>
    </div>
    <div
      class="sky-popover-arrow"
      [style.top.px]="arrowTop"
      [style.left.px]="arrowLeft"
      aria-hidden="true"
      #popoverArrow></div>
  </div>
</div>
`,
  styles: [`.sky-popover-container {
  position: fixed;
  z-index: 999;
  min-width: 276px;
  max-width: 276px;
}

.sky-popover-container.sky-popover-hidden {
  visibility: hidden;
  opacity: 0;
  top: -9999px !important;
  left: -9999px !important;
}

.sky-popover-container:focus {
  outline: none;
}

@media (max-width: 276px) {
  .sky-popover-container {
    max-width: 100%;
    min-width: auto;
  }
}

.sky-popover {
  background-color: #ffffff;
}

.sky-popover-header {
  padding: 10px 10px 0 10px;
}

.sky-popover-header + .sky-popover-body {
  padding-top: 2px;
}

.sky-popover-title {
  font-family: "Blackbaud Sans", "Helvetica Neue", Arial, sans-serif;
  color: #282b31;
  font-weight: 600;
  font-size: 16px;
  margin: 0;
}

.sky-popover-body {
  padding: 10px;
}

.sky-popover-arrow {
  width: 0;
  height: 0;
  position: absolute;
  border: 10px solid transparent;
}

.sky-popover-placement-fullscreen {
  background-color: rgba(0, 0, 0, 0.2);
  padding: 10px !important;
  top: 0 !important;
  left: 0 !important;
  right: 0;
  bottom: 0;
  width: 100% !important;
  max-width: none;
  max-height: 100%;
  height: auto !important;
}

.sky-popover-placement-fullscreen > .sky-popover {
  border-top: 1px solid #cdcfd2;
  border-bottom: 1px solid #cdcfd2;
  border-left: 1px solid #cdcfd2;
  border-right: 1px solid #cdcfd2;
  overflow: auto;
  max-height: 100%;
  display: flex;
  flex-direction: column;
}

.sky-popover-placement-fullscreen > .sky-popover .sky-popover-header {
  padding: 10px;
  border-bottom: 1px solid #e2e3e4;
}

.sky-popover-placement-fullscreen > .sky-popover .sky-popover-body {
  position: relative;
}

.sky-popover-placement-above {
  padding-bottom: 10px;
}

.sky-popover-placement-above .sky-popover {
  border-bottom: 10px solid #00b4f1;
}

.sky-popover-placement-above .sky-popover-arrow {
  border-bottom: 0;
  border-top-color: #00b4f1;
  bottom: 0;
  left: 50%;
  margin-left: -10px;
}

.sky-popover-placement-below {
  padding-top: 10px;
}

.sky-popover-placement-below .sky-popover {
  border-top: 10px solid #00b4f1;
}

.sky-popover-placement-below .sky-popover-arrow {
  border-top: 0;
  border-bottom-color: #00b4f1;
  top: 0;
  left: 50%;
  margin-left: -10px;
}

.sky-popover-placement-right {
  padding-left: 10px;
}

.sky-popover-placement-right .sky-popover {
  border-left: 10px solid #00b4f1;
}

.sky-popover-placement-right .sky-popover-arrow {
  border-left: 0;
  border-right-color: #00b4f1;
  left: 0;
  top: 50%;
  margin-top: -10px;
}

.sky-popover-placement-left {
  padding-right: 10px;
}

.sky-popover-placement-left .sky-popover {
  border-right: 10px solid #00b4f1;
}

.sky-popover-placement-left .sky-popover-arrow {
  border-right: 0;
  border-left-color: #00b4f1;
  right: 0;
  top: 50%;
  margin-top: -10px;
}

.sky-popover-placement-above.sky-popover-alignment-left .sky-popover-arrow,
.sky-popover-placement-below.sky-popover-alignment-left .sky-popover-arrow {
  left: 40px;
  right: auto;
}

.sky-popover-placement-above.sky-popover-alignment-right .sky-popover-arrow,
.sky-popover-placement-below.sky-popover-alignment-right .sky-popover-arrow {
  left: auto;
  right: 40px;
}
`],
  providers: [SkyPopoverAdapterService],
  animations: [
    trigger('popoverState', [
      state('visible', style({ opacity: 1, visibility: 'visible' })),
      state('hidden', style({ opacity: 0 })),
      transition('hidden => visible', animate('150ms')),
      transition('visible => hidden', animate('150ms'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: SkyPopoverAdapterService, },
{type: ChangeDetectorRef, },
{type: ElementRef, },
{type: SkyWindowRefService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'dismissOnBlur': [{ type: Input },],
'popoverTitle': [{ type: Input },],
'alignment': [{ type: Input },],
'placement': [{ type: Input },],
'popoverOpened': [{ type: Output },],
'popoverClosed': [{ type: Output },],
'popoverContainer': [{ type: ViewChild, args: ['popoverContainer', ] },],
'popoverArrow': [{ type: ViewChild, args: ['popoverArrow', ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
